import { useState, useEffect, useMemo, useCallback, Fragment } from "react";

// §5.5 ERP-аудит: детерминированный псевдо-random в [0,1) по строковому seed.
// Раньше демо-наполнение звало Math.random() прямо в рендере → суммы «прыгали»
// между ре-рендерами (несолидно на демо-презентациях). Теперь стабильно по seed.
// FNV-1a хэш — НЕ для крипто/безопасности, только демо-наполнение.
function seededRand(seed) {
  let h = 2166136261;
  const s = String(seed);
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return ((h >>> 0) % 1000000) / 1000000;
}

// Русское склонение числительных: plural(n, "запись","записи","записей").
// Раньше везде было «N записей» — для 1/2/21 читалось коряво («1 записей»).
function plural(n, one, few, many) {
  const n10 = n % 10, n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return one;
  if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return few;
  return many;
}

// ═══ API LAYER ════════════════════════════════════════════════
const API_BASE = "/api/v1";
// ─── TOKEN STORAGE ────────────────────────────────────────────
const TOKEN_KEY = "payroll_token";
const REFRESH_KEY = "payroll_refresh"; // R75 Phase 2c
const ACTING_TENANT_KEY = "payroll_acting_tenant"; // R75 Phase 2f: admin X-Tenant-Id override
let _apiMode = false;

function getToken() { return localStorage.getItem(TOKEN_KEY); }
function setToken(t) { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); }
function getRefreshToken() { return localStorage.getItem(REFRESH_KEY); }
function setRefreshToken(t) { if (t) localStorage.setItem(REFRESH_KEY, t); else localStorage.removeItem(REFRESH_KEY); }
function getActingTenantId() { const v = localStorage.getItem(ACTING_TENANT_KEY); return v ? Number(v) : null; }
function setActingTenantId(id) { if (id) localStorage.setItem(ACTING_TENANT_KEY, String(id)); else localStorage.removeItem(ACTING_TENANT_KEY); }

// R75 Phase 2c: token refresh — silent renew на 401 (вместо kick to login).
// Guard против concurrent refresh: все 401s ждут одного refresh promise.
let _refreshPromise = null;
async function tryRefreshToken() {
  if (_refreshPromise) return _refreshPromise;
  const rt = getRefreshToken();
  if (!rt) return null;
  _refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: rt }),
      });
      if (!res.ok) {
        setRefreshToken(null);
        return null;
      }
      const data = await res.json();
      if (data.accessToken) {
        setToken(data.accessToken);
        if (data.refreshToken) setRefreshToken(data.refreshToken); // R63: rotation
        return data.accessToken;
      }
      return null;
    } catch {
      setRefreshToken(null);
      return null;
    } finally {
      _refreshPromise = null;
    }
  })();
  return _refreshPromise;
}

// ─── API CLIENT ───────────────────────────────────────────────
async function api(path, opts = {}, _retry = false) {
  const token = getToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  // R75 Phase 2f: platform admin может «играть» tenant через X-Tenant-Id.
  // Backend tenant-context.middleware игнорирует header для regular users → защита.
  const actingTenant = getActingTenantId();
  if (actingTenant) headers["X-Tenant-Id"] = String(actingTenant);
  try {
    const res = await fetch(`${API_BASE}${path}`, { ...opts, headers });
    if (res.status === 401) {
      // R75 Phase 2c: на 401 — silent refresh + retry once вместо kick to login
      if (!_retry) {
        const newToken = await tryRefreshToken();
        if (newToken) return api(path, opts, true);
      }
      setToken(null);
      setRefreshToken(null);
      _apiMode = false;
      window.dispatchEvent(new CustomEvent("payroll:logout"));
      throw new Error("Unauthorized");
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `API ${res.status}`);
    }
    if (res.status === 204) return null;
    // R118: защита от не-JSON ответа. Когда API недоступен (демо-режим, сбой прокси),
    // dev-сервер/nginx отдаёт index.html — res.json() бросал «Unexpected token '<'… is
    // not valid JSON» прямо в UI. Отдаём чистое сообщение вместо стектрейса парсинга.
    const ct = res.headers.get("content-type") || "";
    if (!ct.includes("json")) {
      throw new Error("Сервер недоступен");
    }
    return await res.json();
  } catch (e) {
    console.warn(`API call failed: ${path}`, e.message);
    throw e;
  }
}

async function apiLogin(login, password) {
  try {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ login, password }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    // H-R2-2 + H-R3-3 (Codex R3): handle 2FA step-up + enrollment.
    // Enrollment: backend sends accessToken (limited к 2fa.enroll permission).
    // Challenge: backend sends mfaChallengeToken.
    if (data.mfaRequired) {
      if (data.mfaEnrollmentRequired && data.accessToken) {
        // Limited token позволяет вызвать /auth/2fa/setup + /auth/2fa/confirm
        setToken(data.accessToken);
      }
      return {
        mfaRequired: true,
        mfaChallengeToken: data.mfaChallengeToken,
        mfaEnrollmentRequired: !!data.mfaEnrollmentRequired,
        enrollmentToken: data.accessToken,
      };
    }
    if (data.accessToken) {
      setToken(data.accessToken);
      // R75 Phase 2c: persist refresh token для silent renew
      if (data.refreshToken) setRefreshToken(data.refreshToken);
      return data;
    }
    return null;
  } catch { return null; }
}

async function apiVerifyTotp(mfaChallengeToken, totpCode) {
  try {
    const res = await fetch(`${API_BASE}/auth/verify-totp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mfaChallengeToken, totpCode }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `TOTP verify failed: ${res.status}`);
    }
    const data = await res.json();
    if (data.accessToken) {
      setToken(data.accessToken);
      if (data.refreshToken) setRefreshToken(data.refreshToken);
      return data;
    }
    return null;
  } catch (e) { throw e; }
}

async function apiGet(path) { return api(path); }
async function apiPost(path, body) { return api(path, { method: "POST", body: JSON.stringify(body) }); }

// Cursor R2 #20: helper для получения employeeId/userId из JWT.
// R8-5 fix: base64url → standard base64 (- → +, _ → /, add padding) перед atob().
function decodeJwtPayload(token) {
  try {
    const segment = token.split('.')[1];
    if (!segment) return null;
    // base64url → base64
    let b64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    // pad to multiple of 4
    while (b64.length % 4) b64 += '=';
    return JSON.parse(atob(b64));
  } catch { return null; }
}
function getMe() {
  const tok = getToken();
  if (!tok) return null;
  const payload = decodeJwtPayload(tok);
  if (!payload) return null;
  return { userId: payload.sub, employeeId: payload.employeeId, login: payload.login, roles: payload.roles, permissions: payload.permissions };
}
async function apiPut(path, body) { return api(path, { method: "PUT", body: JSON.stringify(body) }); }
async function apiPatch(path, body={}) { return api(path, { method: "PATCH", body: JSON.stringify(body) }); }
async function apiDelete(path) { return api(path, { method: "DELETE" }); }

// ─── TOAST NOTIFICATIONS ──────────────────────────────────────
let _toastFn = null;
function toast(msg, type="info") { if (_toastFn) _toastFn(msg, type); }
function toastOk(msg) { toast(msg, "ok"); }
function toastErr(msg) { toast(msg, "err"); }
function toastInfo(msg) { toast(msg, "info"); }

// ─── TOAST COMPONENT ──────────────────────────────────────────
function ToastContainer() {
  const [toasts, setToasts] = useState([]);
  useEffect(() => {
    _toastFn = (msg, type) => {
      const id = Date.now();
      setToasts(prev => [...prev, { id, msg, type }]);
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
    };
    return () => { _toastFn = null; };
  }, []);
  if (!toasts.length) return null;
  return (
    <div style={{ position:"fixed", bottom:24, right:24, zIndex:9999, display:"flex", flexDirection:"column", gap:8 }}>
      {toasts.map(t => (
        <div key={t.id} style={{ padding:"10px 16px", borderRadius:8, fontSize:13, fontFamily:"'IBM Plex Sans',sans-serif",
          background: t.type==="ok" ? "#166534" : t.type==="err" ? "#7B1414" : "#1E3A5F",
          color:"#fff", border:`1px solid ${t.type==="ok"?"#22C55E":t.type==="err"?"#EF4444":"#2979FF"}40`,
          boxShadow:"0 4px 12px rgba(0,0,0,0.4)", maxWidth:340 }}>
          {t.type==="ok" ? "✅ " : t.type==="err" ? "❌ " : "ℹ️ "}{t.msg}
        </div>
      ))}
    </div>
  );
}

// ═══ T-31: EMPTY STATE COMPONENT ════════════════════════
function EmptyState({ icon="📭", title="Нет данных", sub="", action=null }) {
  return (
    <div style={{ textAlign:"center", padding:"40px 20px", color:C.muted }}>
      <div style={{ fontSize:40, marginBottom:12 }}>{icon}</div>
      <div style={{ fontSize:14, fontWeight:600, marginBottom:4 }}>{title}</div>
      {sub && <div style={{ fontSize:11 }}>{sub}</div>}
      {action && <div style={{ marginTop:14 }}>{action}</div>}
    </div>
  );
}

// R74 primitives moved after const S definition (TDZ fix)
// ═══ T-42: LOADING SKELETON ═════════════════════════════
function LoadingSkeleton({ rows=5, cols=4 }) {
  return (
    <div style={{ padding:8 }}>
      {Array.from({length: rows}).map((_, i) => (
        <div key={i} style={{ display:"flex", gap:12, marginBottom:12, animation:"pulse 1.5s infinite" }}>
          {Array.from({length: cols}).map((_, j) => (
            <div key={j} style={{ flex: j===0 ? 0.5 : 1, height:16, borderRadius:4, background:C.bg3 }} />
          ))}
        </div>
      ))}
      <style>{`@keyframes pulse { 0%,100% { opacity:0.4; } 50% { opacity:0.8; } }`}</style>
    </div>
  );
}

// ═══ T-32: CONFIRM MODAL COMPONENT ══════════════════════
function ConfirmModal({ title, message, onConfirm, onCancel, danger=false }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:1000, display:"flex", justifyContent:"center", alignItems:"center" }} onClick={onCancel}>
      <div style={{ background:C.card, border:`1px solid ${danger ? C.red : C.border}`, borderRadius:12, padding:24, width:420 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize:16, fontWeight:700, marginBottom:12, color: danger ? C.red : C.txt }}>{title}</div>
        <div style={{ fontSize:13, color:C.muted, marginBottom:20 }}>{message}</div>
        <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
          <button style={{...S.btn(C.bg3)}} onClick={onCancel}>Отмена</button>
          <button style={{...S.btn(danger ? C.red : C.green)}} onClick={onConfirm}>
            {danger ? "Подтвердить" : "OK"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══ DESIGN SYSTEM ═══════════════════════════════════════════
const C = {
  bg: "#0B1120", bg2: "#111827", bg3: "#1F2937",
  card: "#151E2E", border: "#1E3A5F", accent: "#2979FF",
  green: "#22C55E", red: "#EF4444", amber: "#F59E0B", purple: "#A78BFA",
  teal: "#14B8A6", pink: "#EC4899", coral: "#F97316",
  txt: "#F1F5F9", muted: "#64748B", dim: "#334155",
};
const F = "'IBM Plex Sans', system-ui, sans-serif";
const M = "'IBM Plex Mono', monospace";

// ═══ DEMO DATA (mirrors seed.ts) ════════════════════════════
// §5.6 ERP-аудит: generic демо-ТОО (торгово-производственное, ~30 чел) вместо
// банковского штата — релевантно пилотным ААС-клиентам (ТОО 30-100 чел), не смущает
// бухгалтера банковскими C-level/Комплаенс/ПОД-ФТ. Данные вымышленные (demo only).
const DEPTS = [
  { code:"ADMIN", name:"Администрация", head:2, fot:1350000 },
  { code:"ACC", name:"Бухгалтерия", head:3, fot:1050000 },
  { code:"SALES", name:"Отдел продаж", head:4, fot:1400000 },
  { code:"WAREHOUSE", name:"Склад", head:3, fot:760000 },
  { code:"PROD", name:"Производство", head:5, fot:1380000 },
  { code:"SUPPLY", name:"Снабжение", head:2, fot:580000 },
  { code:"TRANSPORT", name:"Транспортный отдел", head:3, fot:700000 },
  { code:"HR", name:"Отдел кадров", head:1, fot:340000 },
];

const EMPLOYEES = [
  { id:1, name:"Алиев Касым С.", dept:"Администрация", pos:"Директор", grade:"8", salary:750000, status:"active" },
  { id:2, name:"Сериков Бакытжан М.", dept:"Администрация", pos:"Заместитель директора", grade:"7", salary:600000, status:"active" },
  { id:3, name:"Нурланова Динара Е.", dept:"Бухгалтерия", pos:"Главный бухгалтер", grade:"6", salary:500000, status:"active" },
  { id:4, name:"Токтаров Ержан К.", dept:"Бухгалтерия", pos:"Бухгалтер", grade:"4", salary:350000, status:"active" },
  { id:5, name:"Касымова Айгуль Б.", dept:"Отдел продаж", pos:"Руководитель отдела продаж", grade:"6", salary:550000, status:"active" },
  { id:6, name:"Жумабеков Арман К.", dept:"Отдел продаж", pos:"Менеджер по продажам", grade:"4", salary:350000, status:"active" },
  { id:7, name:"Идрисов Нурлан Ж.", dept:"Производство", pos:"Мастер цеха", grade:"5", salary:400000, status:"active" },
  { id:8, name:"Оспанов Данияр Б.", dept:"Склад", pos:"Заведующий складом", grade:"4", salary:330000, status:"active" },
  { id:9, name:"Бекмуратова Алия Н.", dept:"Склад", pos:"Кладовщик", grade:"2", salary:240000, status:"active" },
  { id:10, name:"Кенжебаев Руслан С.", dept:"Транспортный отдел", pos:"Водитель", grade:"2", salary:260000, status:"active" },
  { id:11, name:"Муканова Жанна С.", dept:"Отдел кадров", pos:"Инспектор по кадрам", grade:"4", salary:320000, status:"active" },
  { id:12, name:"Ибрагимов Марат Т.", dept:"Снабжение", pos:"Специалист по снабжению", grade:"3", salary:300000, status:"active" },
];

const PIPELINE_STEPS = [
  { step:1, label:"Табель", icon:"📋", status:"done", desc:"Автозаполнено", color:C.green },
  { step:2, label:"Проверка", icon:"🔍", status:"done", desc:"36 из 36", color:C.green },
  { step:3, label:"Расчёт", icon:"🧮", status:"done", desc:"НК РК 2026", color:C.green },
  { step:4, label:"GL проводки", icon:"📊", status:"done", desc:"324 проводки", color:C.green },
  { step:5, label:"Утверждение", icon:"✅", status:"current", desc:"Ожидает CHRO", color:C.amber },
  { step:6, label:"Выплата", icon:"💳", status:"pending", desc:"Colvir CBS", color:C.dim },
  { step:7, label:"Закрытие", icon:"🔒", status:"pending", desc:"Блокировка", color:C.dim },
];

const MRP = 4325, MZP = 85000;
// R112 §5.3 (аудит безопасности): demo-вход и подсказка кред — только в dev/demo-сборке.
// В прод-бандле REACT_APP_DEMO не задан → false → вход только по реальному JWT (банковский продукт).
const DEMO_ENABLED = process.env.REACT_APP_DEMO === "1";
// T-14: calcPayroll — формулы НК РК 2026, верифицированы против 1С:ЗУП
function calcPayroll(salary) {
  const opv = Math.round(Math.min(salary, 50*MZP) * 0.10);
  const vosms = Math.round(Math.min(Math.max(salary, MZP), 20*MZP) * 0.02); // clamp(salary, MZP, 20×МЗП)
  const deduction = 30 * MRP; // 129750₸
  const taxable = Math.max(salary - opv - vosms - deduction, 0);
  // ИПН: 10% если база ≤ 8500×МРП (36762500₸), 15% на сверх
  const ipnThreshold = 8500 * MRP;
  const ipn = taxable <= ipnThreshold
    ? Math.round(taxable * 0.10)
    : Math.round(ipnThreshold * 0.10 + (taxable - ipnThreshold) * 0.15);
  const net = salary - opv - vosms - ipn;
  // СН: 6% от max(Доход−ОПВ−ВОСМС, 14×МРП)
  const sn = Math.round(Math.max(salary - opv - vosms, 14 * MRP) * 0.06);
  const so = Math.round(Math.min(salary - opv, 7*MZP) * 0.05);
  const oosms = Math.round(Math.min(salary, 40*MZP) * 0.03);
  const opvr = Math.round(Math.min(salary, 50*MZP) * 0.035);
  return { salary, opv, vosms, ipn, net, sn, so, oosms, opvr, deduction, taxable, ipnThreshold };
}

// T-14: Верификация — эталонные значения для оклада 550000₸ (из ТЗ)
const VERIFY_SALARY = 550000;
// ТЗ v3: ИПН=35425 (опечатка в v2 исправлена), net=448575
const VERIFY_EXPECTED = { opv:55000, vosms:11000, ipn:35425, net:448575, sn:29040, so:24750, oosms:16500, opvr:19250 };
function verifyTaxFormulas() {
  const r = calcPayroll(VERIFY_SALARY);
  const checks = Object.entries(VERIFY_EXPECTED).map(([k,v]) => ({ key:k, expected:v, actual:r[k], ok: r[k]===v }));
  return { allOk: checks.every(c => c.ok), checks, result: r };
}

const FUNNEL = [
  { stage:"Новые", count:12, color:"#3B82F6" },
  { stage:"Скрининг", count:8, color:"#8B5CF6" },
  { stage:"Собеседование", count:5, color:"#F59E0B" },
  { stage:"Тестирование", count:3, color:"#EC4899" },
  { stage:"Оффер", count:2, color:"#14B8A6" },
  { stage:"Принят", count:1, color:"#22C55E" },
];

// ═══ STYLES ═════════════════════════════════════════════════
const S = {
  root: { fontFamily:F, background:C.bg, color:C.txt, minHeight:"100vh", display:"flex" },
  sidebar: { width:220, background:C.bg2, borderRight:`1px solid ${C.border}`, padding:"16px 0", flexShrink:0, display:"flex", flexDirection:"column" },
  logo: { padding:"0 16px 20px", fontSize:13, fontWeight:700, letterSpacing:"0.05em", color:C.accent, borderBottom:`1px solid ${C.border}`, marginBottom:8 },
  navItem: (active) => ({
    padding:"10px 16px", fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", gap:10,
    background: active ? `${C.accent}15` : "transparent",
    color: active ? C.accent : C.muted,
    borderLeft: active ? `3px solid ${C.accent}` : "3px solid transparent",
    transition:"all 0.15s",
  }),
  // §5.7 a11y: reset нативной <button> до вида div (семантика+клавиатура без смены вида).
  // Стили navItem/группы накладываются ПОВЕРХ (background/color/borderLeft переопределяют reset).
  navBtnReset: { borderTop:"none", borderRight:"none", borderBottom:"none", font:"inherit", fontFamily:"inherit", margin:0, width:"100%", appearance:"none", outlineOffset:"-2px" },
  main: { flex:1, padding:24, overflowY:"auto", maxHeight:"100vh" },
  pageTitle: { fontSize:18, fontWeight:700, marginBottom:4 },
  pageSub: { fontSize:12, color:C.muted, marginBottom:20 },
  card: { background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:16, marginBottom:14 },
  kpi: (c) => ({ background:`${c}10`, border:`1px solid ${c}30`, borderRadius:10, padding:"14px 18px", textAlign:"center" }),
  kpiVal: (c) => ({ fontSize:28, fontWeight:800, color:c, fontFamily:M }),
  kpiLabel: { fontSize:10, color:C.muted, marginTop:4, letterSpacing:"0.04em", textTransform:"uppercase" },
  tableWrap: { overflowX:"auto", WebkitOverflowScrolling:"touch" },
  table: { width:"100%", borderCollapse:"collapse", fontSize:12, minWidth:600 },
  th: { padding:"8px 12px", textAlign:"left", borderBottom:`1px solid ${C.border}`, color:C.muted, fontWeight:600, fontSize:10, letterSpacing:"0.05em", textTransform:"uppercase" },
  td: { padding:"8px 12px", borderBottom:`1px solid ${C.border}22` },
  badge: (c) => ({ display:"inline-block", padding:"2px 8px", borderRadius:4, fontSize:10, fontWeight:600, background:`${c}20`, color:c, border:`1px solid ${c}40` }),
  btn: (c=C.accent) => ({ background:c, color:"#fff", border:"none", borderRadius:6, padding:"8px 16px", fontSize:12, fontWeight:600, cursor:"pointer" }),
  input: { background:C.bg3, border:`1px solid ${C.border}`, borderRadius:6, padding:"8px 12px", color:C.txt, fontSize:13, fontFamily:F, width:"100%", outline:"none" },
  lbl: { fontSize:11, color:C.muted, marginBottom:4 },
  bar: (pct, c) => ({ width:`${pct}%`, height:6, background:c, borderRadius:3, transition:"width 0.5s" }),
};


// ═══ R74 ERP PRIMITIVES (Status / Sort / Search / Filter / Detail / Table / Header) ═══

// Status pill with semantic colors per category (lifecycle/payment/hr)
const STATUS_COLORS = {
  draft: C.amber, approved: C.accent, applied: C.green, superseded: C.dim,
  active: C.green, archived: C.dim, cancelled: C.red, rejected: C.red,
  completed: C.green, commission_assembled: C.accent, paid: C.green,
  pending: C.amber, sent: C.teal, returned: C.amber, voided: C.red,
  reversed: C.dim, deposited: C.purple, reissued: C.teal,
  // payment statement
  partially_completed: C.amber,
  posted: C.green,
  written_off: C.dim,
  terminated: C.dim,
  // R81 backend-ready screens
  preview: C.teal, executed: C.green, resumed: C.green, adjusted: C.amber,
  // R82 vacation-schedule
  planned: C.amber, confirmed: C.accent, rescheduled: C.teal, taken: C.green,
  // R83
  returned: C.teal,
  // R84
  needs_action: C.amber, waived: C.dim,
  // R84 batch2
  used: C.dim, registered: C.amber, verified: C.green, issued: C.green,
  // R84 batch3
  reported: C.amber, investigating: C.accent, resolved: C.green, closed: C.dim,
  suspended: C.amber, evaluated: C.green, distributed: C.green, calculated: C.amber, accrued: C.teal,
};
const STATUS_LABELS_RU = {
  draft: "Черновик", approved: "Утверждён", applied: "Применён", superseded: "Заменён",
  active: "Активно", archived: "Архив", cancelled: "Отменён", rejected: "Отклонён",
  completed: "Завершён", commission_assembled: "Комиссия собрана", paid: "Оплачен",
  pending: "Ожидает", sent: "Отправлен", returned: "Возвращён", voided: "Аннулирован",
  reversed: "Сторно", deposited: "Депонирован", reissued: "Перевыпущен",
  partially_completed: "Частично",
  posted: "Проведён",
  written_off: "Списан",
  terminated: "Прекращён",
  // R81 backend-ready screens
  preview: "Предпросмотр", executed: "Исполнен", resumed: "Возобновлён", adjusted: "Скорректирован",
  // R82 vacation-schedule
  planned: "Запланирован", confirmed: "Подтверждён", rescheduled: "Перенесён", taken: "Использован",
  // R83
  returned: "Вышел из отпуска",
  // R84
  needs_action: "Требует действия", waived: "Списана",
  // R84 batch2
  used: "Использован", registered: "Зарегистрирована", verified: "Проверена", issued: "Выдана",
  // R84 batch3
  reported: "Зарегистрирован", investigating: "Расследуется", resolved: "Расследован", closed: "Закрыт",
  suspended: "Приостановлен", evaluated: "Оценён", distributed: "Распределён", calculated: "Рассчитан", accrued: "Начислен",
};
function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || C.muted;
  const label = STATUS_LABELS_RU[status] || status;
  return <span style={S.badge(c)}>{label}</span>;
}

// Sortable column header — click to toggle asc/desc
function SortableHeader({ label, field, sort, setSort, align="left" }) {
  const active = sort?.field === field;
  const arrow = !active ? "" : sort.dir === "asc" ? " ▲" : " ▼";
  return (
    <th style={{...S.th, textAlign:align, cursor:"pointer", userSelect:"none"}}
        onClick={() => setSort(active && sort.dir==="asc" ? { field, dir:"desc" } : { field, dir:"asc" })}>
      {label}<span style={{color:C.accent}}>{arrow}</span>
    </th>
  );
}

// Generic search input с clear button
function SearchInput({ value, onChange, placeholder="Поиск...", width=240 }) {
  return (
    <div style={{ position:"relative", width }}>
      <input style={{...S.input, paddingLeft:30, paddingRight: value ? 28 : 12}}
             value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} />
      <span style={{ position:"absolute", left:9, top:8, fontSize:13, color:C.muted, pointerEvents:"none" }}>🔍</span>
      {value && <span style={{ position:"absolute", right:9, top:7, fontSize:14, color:C.muted, cursor:"pointer" }}
                       onClick={()=>onChange("")}>×</span>}
    </div>
  );
}

// FilterBar — set of dropdown filters + Reset
function FilterBar({ filters, values, onChange, onReset }) {
  const active = Object.values(values).filter(v => v !== "" && v != null).length;
  return (
    <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:12 }}>
      {filters.map(f => (
        <div key={f.key} style={{ display:"flex", alignItems:"center", gap:6 }}>
          <span style={{ fontSize:11, color:C.muted }}>{f.label}:</span>
          <select style={{...S.input, width:f.width||140, padding:"6px 10px", fontSize:12}}
                  value={values[f.key] ?? ""} onChange={e=>onChange({...values, [f.key]: e.target.value})}>
            <option value="">— все —</option>
            {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      ))}
      {active > 0 && (
        <button style={{ ...S.btn(C.bg3), padding:"6px 10px", fontSize:11, color:C.muted, border:`1px solid ${C.border}` }}
                onClick={onReset}>✕ Сбросить ({active})</button>
      )}
    </div>
  );
}

// Slide-in detail panel from right
function DetailPanel({ open, onClose, title, children, width=420 }) {
  if (!open) return null;
  return (
    <>
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:9000 }} onClick={onClose} />
      <div style={{ position:"fixed", top:0, right:0, bottom:0, width, background:C.card,
                    borderLeft:`1px solid ${C.border}`, boxShadow:"-8px 0 32px rgba(0,0,0,0.5)",
                    zIndex:9001, overflowY:"auto", padding:0 }}>
        <div style={{ padding:"14px 18px", borderBottom:`1px solid ${C.border}`, display:"flex",
                      justifyContent:"space-between", alignItems:"center", position:"sticky", top:0, background:C.card }}>
          <div style={{ fontSize:14, fontWeight:700 }}>{title}</div>
          <button style={{ background:"transparent", border:"none", color:C.muted, fontSize:18, cursor:"pointer" }} onClick={onClose}>×</button>
        </div>
        <div style={{ padding:18 }}>{children}</div>
      </div>
    </>
  );
}

// Page header с title, subtitle и optional toolbar
function PageHeader({ title, sub, mode, count, toolbar }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16, flexWrap:"wrap", gap:12 }}>
      <div>
        <div style={S.pageTitle}>{title}</div>
        <div style={S.pageSub}>
          {sub}
          {typeof count === "number" && <span style={{ marginLeft:6, color:C.accent }}>· {count} {plural(count, "запись", "записи", "записей")}</span>}
          {mode && <span style={{ marginLeft:6 }}>· {mode === "demo" ? "(demo)" : "(live)"}</span>}
        </div>
      </div>
      {toolbar && <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>{toolbar}</div>}
    </div>
  );
}

// Generic ResourceTable с sort + pagination + row click
function ResourceTable({ rows, columns, onRowClick, sort, setSort, pageSize=25, getRowKey, emptyIcon="📭", emptyTitle="Нет данных", emptySub="" }) {
  const [page, setPage] = useState(0);
  // Reset page when row count changes drastically
  useEffect(() => { if (page > 0 && page * pageSize >= rows.length) setPage(0); }, [rows.length, page, pageSize]);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const start = page * pageSize;
  const visible = rows.slice(start, start + pageSize);
  if (rows.length === 0) {
    return <EmptyState icon={emptyIcon} title={emptyTitle} sub={emptySub} />;
  }
  return (
    <div style={S.card}>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              {columns.map(col => col.sortable
                ? <SortableHeader key={col.field || col.label} label={col.label} field={col.field} sort={sort} setSort={setSort} align={col.align} />
                : <th key={col.field || col.label} style={{...S.th, textAlign: col.align || "left"}}>{col.label}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {visible.map(row => (
              <tr key={getRowKey(row)}
                  style={{ cursor: onRowClick ? "pointer" : "default", transition:"background 0.1s" }}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onMouseEnter={e => { if (onRowClick) e.currentTarget.style.background = `${C.accent}08`; }}
                  onMouseLeave={e => { if (onRowClick) e.currentTarget.style.background = "transparent"; }}>
                {columns.map(col => (
                  <td key={col.field || col.label}
                      style={{...S.td, textAlign: col.align || "left", ...(col.cellStyle || {})}}>
                    {col.render ? col.render(row) : (col.field ? row[col.field] : "—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 4px 0",
                      fontSize:11, color:C.muted, borderTop:`1px solid ${C.border}40`, marginTop:8 }}>
          <div>Показано {start+1}–{Math.min(start+pageSize, rows.length)} из {rows.length}</div>
          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
            <button style={{...S.btn(C.bg3), padding:"4px 10px", fontSize:11, opacity: page===0?0.4:1, border:`1px solid ${C.border}`}}
                    disabled={page===0} onClick={()=>setPage(p=>p-1)}>← Назад</button>
            <span style={{ fontFamily:M, fontSize:11 }}>{page+1} / {totalPages}</span>
            <button style={{...S.btn(C.bg3), padding:"4px 10px", fontSize:11, opacity: page+1>=totalPages?0.4:1, border:`1px solid ${C.border}`}}
                    disabled={page+1>=totalPages} onClick={()=>setPage(p=>p+1)}>Вперёд →</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Pure sort utility — multi-type aware
function sortRows(rows, sort) {
  if (!sort?.field) return rows;
  const { field, dir } = sort;
  return [...rows].sort((a, b) => {
    let av = a[field], bv = b[field];
    if (av == null && bv == null) return 0;
    if (av == null) return 1; if (bv == null) return -1;
    if (typeof av === "number" && typeof bv === "number") return dir==="asc" ? av-bv : bv-av;
    av = String(av); bv = String(bv);
    return dir==="asc" ? av.localeCompare(bv, "ru") : bv.localeCompare(av, "ru");
  });
}

// Filter rows by multiple criteria + search string
function filterRows(rows, filterValues, searchValue, searchFields) {
  return rows.filter(r => {
    for (const [key, val] of Object.entries(filterValues)) {
      if (val !== "" && val != null) {
        const rowVal = r[key];
        if (rowVal == null) return false;
        if (String(rowVal) !== String(val)) return false;
      }
    }
    if (searchValue && searchFields) {
      const lower = searchValue.toLowerCase();
      const matches = searchFields.some(f => String(r[f] ?? "").toLowerCase().includes(lower));
      if (!matches) return false;
    }
    return true;
  });
}

// Format helpers reused by screens
const fmtNumRu = v => Number(v).toLocaleString("ru-RU");
const fmtDate = d => d ? String(d).slice(0, 10) : "—";
const todayISO = () => new Date().toISOString().slice(0, 10);

// ─── R75 Phase 2g: reference-data loaders для create-форм ────
// Централизуют точные shapes backend-эндпоинтов (один источник правды).
// Verified против DTO модулей: department, position, staff-grade, employee,
// work-location, staff/schedule.
async function loadDepartmentOptions() {
  const d = await apiGet("/departments?limit=200");
  const list = Array.isArray(d) ? d : (d?.items ?? []);
  return list.map(x => ({ id: x.id, code: x.code, nameRu: x.nameRu || x.name_ru || x.name || `#${x.id}` }));
}
async function loadPositionOptions() {
  const d = await apiGet("/positions?limit=500");
  const list = Array.isArray(d) ? d : (d?.items ?? []);
  return list.map(x => ({ id: x.id, code: x.code, nameRu: x.nameRu || x.name || `#${x.id}`, gradeId: x.gradeId ?? null }));
}
async function loadGradeOptions() {
  // /staff/scales → [{ grades: [{id, code, nameRu, level}] }]
  const scales = await apiGet("/staff/scales");
  const out = [];
  (Array.isArray(scales) ? scales : []).forEach(s =>
    (s.grades || []).forEach(g => out.push({ id: g.id, code: g.code, nameRu: g.nameRu || g.name || g.code })));
  return out;
}
async function loadEmployeeOptions() {
  // R112: /employees/options — облегчённый список без капа пагинации (findAll капил limit до 200 →
  // у тенанта >200 чел dropdown'ы выбора сотрудника теряли людей после 200-го). Состав тот же.
  const d = await apiGet("/employees/options");
  const list = Array.isArray(d) ? d : (d?.items ?? d?.data ?? []);
  return list.map(e => {
    const fio = `${e.lastName || ""} ${e.firstName || ""} ${e.middleName || ""}`.replace(/\s+/g, " ").trim();
    return {
      id: e.id,
      label: fio || `Сотрудник #${e.id}`,
      iin: e.iin || "",
      departmentId: e.departmentId ?? e.department?.id ?? null,
      workLocationId: e.workLocationId ?? e.workLocation?.id ?? null,
    };
  });
}
async function loadWorkLocationOptions() {
  const d = await apiGet("/work-location");
  const list = Array.isArray(d) ? d : (d?.items ?? []);
  return list.map(w => ({ id: w.id, code: w.code, nameRu: w.nameRu || w.name || `#${w.id}`, isActive: w.isActive !== false }));
}
async function loadStaffPositionOptions() {
  // /staff/schedule → { positions: [{id, staffUnits, filledUnits, department, position}] }
  const d = await apiGet("/staff/schedule");
  const list = d?.positions ?? [];
  return list.map(p => {
    const dep = p.department?.nameRu || p.department?.code || "—";
    const pos = p.position?.nameRu || p.position?.code || "—";
    const vacant = Number(p.staffUnits || 0) - Number(p.filledUnits || 0);
    return { id: p.id, label: `${dep} · ${pos}`, vacant: Math.round(vacant * 100) / 100 };
  });
}
async function loadPeriodOptions() {
  const d = await apiGet("/periods");
  const list = Array.isArray(d) ? d : (d?.items ?? d?.data ?? []);
  return list.map(p => ({ id: p.id, label: `${p.year}-${String(p.month).padStart(2, "0")}`, year: p.year, month: p.month }));
}
// Загружает options через loader один раз когда enabled && api-режим.
// loader — стабильная module-level функция, поэтому безопасно вне deps.
function useRefOptions(loader, enabled) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    if (!enabled || !_apiMode) return;
    let alive = true;
    loader().then(d => { if (alive) setOpts(d); }).catch(() => {});
    return () => { alive = false; };
  }, [enabled]);
  return opts;
}


// ═══ SCREENS ════════════════════════════════════════════════

// §8.1-B общий источник «Мои задачи» (виджет Dashboard + колокольчик) — 3 готовых эндпоинта.
// active=false (demo/не залогинен) → null (UI деградирует). Без дублирования fetch-логики.
function useMyTasks(active) {
  const [tasks, setTasks] = useState(null);
  useEffect(() => {
    if (!active) { setTasks(null); return; }
    let cancelled = false;
    (async () => {
      const [approvals, runs, periods] = await Promise.all([
        apiGet("/workflow/pending").catch(() => null),
        apiGet("/payroll-run?limit=50").catch(() => null),
        apiGet("/periods").catch(() => null),
      ]);
      if (cancelled) return;
      if (approvals == null && runs == null && periods == null) { setTasks(null); return; }
      const arr = x => Array.isArray(x) ? x : (x?.items || x?.data || []);
      const now = new Date();
      const curYM = now.getFullYear() * 100 + (now.getMonth() + 1);
      const overdueList = arr(periods).filter(p => p.status !== "closed" && (p.year * 100 + p.month) < curYM);
      const failedList = arr(runs).filter(r => Array.isArray(r.errors) && r.errors.length > 0);
      // R112 §8.1-A: deep-link на приоритетный объект — старейший незакрытый период
      // (дольше всех висит = срочнее) и период первого упавшего расчёта. Клик по задаче
      // ведёт прямо на проблему (#/payroll/<id>), а не на дефолтный последний период.
      const oldestOverdue = overdueList.slice().sort((a, b) => (a.year * 100 + a.month) - (b.year * 100 + b.month))[0];
      setTasks({
        approvals: arr(approvals).length,
        failedRuns: failedList.length,
        failedRunPeriodId: failedList[0]?.periodId ?? null,
        overduePeriods: overdueList.length,
        overduePeriodId: oldestOverdue?.id ?? null,
      });
    })();
    return () => { cancelled = true; };
  }, [active]);
  return tasks;
}

// R114 §nav-IA Этап 2: недавние/избранное экраны (localStorage). Метки/иконки берёт
// потребитель из реестра SCREENS (App.jsx). dashboard в «недавние» не пишем (это дом).
function getNavRecent() { try { return JSON.parse(localStorage.getItem("nav_recent") || "[]"); } catch { return []; } }
function pushNavRecent(page) {
  if (!page || page === "dashboard") return getNavRecent();
  try {
    let r = getNavRecent().filter(p => p !== page);
    r.unshift(page);
    r = r.slice(0, 8);
    localStorage.setItem("nav_recent", JSON.stringify(r));
    return r;
  } catch { return getNavRecent(); }
}
function getNavFavorites() { try { return JSON.parse(localStorage.getItem("nav_fav") || "[]"); } catch { return []; } }
function toggleNavFavorite(page) {
  try {
    const f = getNavFavorites();
    const nf = f.includes(page) ? f.filter(p => p !== page) : [...f, page];
    localStorage.setItem("nav_fav", JSON.stringify(nf));
    return nf;
  } catch { return getNavFavorites(); }
}


const InlineMsg = ({ msg }) => msg ? <div style={{ marginBottom:12, padding:"8px 12px", borderRadius:6, fontSize:12, background: msg.ok?`${C.green}15`:`${C.red}15`, border:`1px solid ${(msg.ok?C.green:C.red)}40`, color: msg.ok?C.green:C.red }}>{msg.text}</div> : null;

const GL_MONTHS = ["Январь","Февраль","Март","Апрель","Май","Июнь","Июль","Август","Сентябрь","Октябрь","Ноябрь","Декабрь"];

function useFormDraft(key, defaultForm, sigFields) {
  const [form, setForm] = useState(() => {
    try { const s = localStorage.getItem(key); if (s) return { ...defaultForm, ...JSON.parse(s) }; } catch {}
    return defaultForm;
  });
  const [restored, setRestored] = useState(false);
  useEffect(() => { try { if (localStorage.getItem(key)) setRestored(true); } catch {} }, [key]);
  useEffect(() => {
    try { if (sigFields.some(f => form[f])) localStorage.setItem(key, JSON.stringify(form)); } catch {}
  }, [form]);
  const clear = () => { try { localStorage.removeItem(key); } catch {} setForm(defaultForm); setRestored(false); };
  return [form, setForm, { restored, clear }];
}

// ─── R75 Phase 2h: print-preview modal (белый документ, изолируется для window.print()) ───
// Используется печатными формами (Т-3 ШР и др.). Шапка .no-print, тело .print-doc.
function PrintPreview({ title, onClose, children }) {
  const tdBtn = { fontSize:12, padding:"6px 12px", borderRadius:6, cursor:"pointer", fontWeight:600 };
  return (
    <div onClick={onClose} style={{position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:2000, display:"flex", justifyContent:"center", alignItems:"flex-start", overflowY:"auto", padding:"24px 0"}}>
      <div onClick={e=>e.stopPropagation()} style={{width:"210mm", maxWidth:"94vw", background:"#fff", color:"#111", borderRadius:6, boxShadow:"0 8px 40px rgba(0,0,0,0.5)", marginBottom:24}}>
        <div className="no-print" style={{display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 16px", borderBottom:"1px solid #ddd", background:"#f5f5f5", borderRadius:"6px 6px 0 0", position:"sticky", top:0}}>
          <span style={{fontSize:13, fontWeight:600, color:"#333"}}>{title}</span>
          <div style={{display:"flex", gap:8}}>
            <button onClick={()=>window.print()} style={{...tdBtn, background:C.accent, color:"#fff", border:"none"}}>🖨 Печать</button>
            <button onClick={onClose} style={{...tdBtn, background:"#e5e5e5", color:"#333", border:"1px solid #ccc"}}>✕ Закрыть</button>
          </div>
        </div>
        <div className="print-doc" style={{padding:"16mm 14mm", fontFamily:"'Times New Roman', Georgia, serif", fontSize:13, lineHeight:1.45, color:"#111"}}>
          {children}
        </div>
      </div>
    </div>
  );
}

// Рендер формы Т-3 (Штатное расписание) из detail-данных staffing-approval (с lines).
function renderStaffingT3(d, orgName) {
  const lines = d.lines || [];
  const cell = { border:"1px solid #333", padding:"4px 6px" };
  const th = { ...cell, background:"#eee", textAlign:"center", fontWeight:700 };
  return (
    <div>
      <div style={{textAlign:"right", marginBottom:18, fontSize:12}}>
        <div style={{fontWeight:700}}>УТВЕРЖДАЮ</div>
        <div style={{marginTop:4}}>Руководитель _______________________</div>
        <div style={{fontSize:10, color:"#444"}}>(подпись / расшифровка)</div>
        <div style={{marginTop:6}}>Приказ № {d.approvalNumber} от {fmtDate(d.approvalDate)}</div>
      </div>
      <div style={{textAlign:"center", fontWeight:700, fontSize:17, letterSpacing:"0.04em"}}>ШТАТНОЕ РАСПИСАНИЕ</div>
      <div style={{textAlign:"center", marginBottom:14, fontSize:11, color:"#333"}}>
        унифицированная форма Т-3 · {d.mode === "amendment" ? "изменение к ШР" : "первоначальное"} · вводится в действие с {fmtDate(d.effectiveFrom)}
      </div>
      <div style={{marginBottom:10, fontSize:12}}>Организация: <b>{orgName || "_______________________________________"}</b></div>
      <table style={{width:"100%", borderCollapse:"collapse", fontSize:11}}>
        <thead>
          <tr>
            <th style={th}>№</th>
            <th style={th}>Структурное подразделение</th>
            <th style={th}>Должность (профессия)</th>
            <th style={th}>Грейд</th>
            <th style={th}>Кол-во шт. ед.</th>
            <th style={th}>Оклад (мин – макс), ₸</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={l.id || i}>
              <td style={{...cell, textAlign:"center"}}>{i + 1}</td>
              <td style={cell}>{l.department?.nameRu || l.department?.code || "—"}</td>
              <td style={cell}>{l.position?.nameRu || l.position?.code || "—"}</td>
              <td style={{...cell, textAlign:"center"}}>{l.grade?.code || "—"}</td>
              <td style={{...cell, textAlign:"center"}}>{Number(l.staffUnits).toFixed(2)}</td>
              <td style={{...cell, textAlign:"right"}}>{fmtNumRu(l.salaryMin)} – {fmtNumRu(l.salaryMax)}</td>
            </tr>
          ))}
          <tr style={{fontWeight:700}}>
            <td colSpan={4} style={{...cell, textAlign:"right"}}>ИТОГО</td>
            <td style={{...cell, textAlign:"center"}}>{Number(d.totalStaffUnits).toFixed(2)}</td>
            <td style={{...cell, textAlign:"right"}}>{fmtNumRu(d.totalSalaryMin)} – {fmtNumRu(d.totalSalaryMax)}</td>
          </tr>
        </tbody>
      </table>
      <div style={{marginTop:10, fontSize:11}}>
        Всего штатных единиц: <b>{Number(d.totalStaffUnits).toFixed(2)}</b> · Месячный ФОТ (мин – макс): <b>{fmtNumRu(d.totalSalaryMin)} – {fmtNumRu(d.totalSalaryMax)} ₸</b>
      </div>
      <div style={{marginTop:40, display:"flex", justifyContent:"space-between", fontSize:12}}>
        <div>Руководитель кадровой службы&nbsp;&nbsp;__________&nbsp;/&nbsp;______________</div>
        <div>Главный бухгалтер&nbsp;&nbsp;__________&nbsp;/&nbsp;______________</div>
      </div>
    </div>
  );
}

// §8.5 code-split: _apiMode — мутируемая модульная переменная, читается во всех экранах.
// ES-импорты read-only → читатели импортируют _apiMode (live binding), писатели зовут setApiMode.
export function setApiMode(v) { _apiMode = v; }
export function getApiMode() { return _apiMode; }

export { seededRand, plural, API_BASE, TOKEN_KEY, REFRESH_KEY, ACTING_TENANT_KEY, _apiMode, getToken, setToken, getRefreshToken, setRefreshToken, getActingTenantId, setActingTenantId, tryRefreshToken, api, apiLogin, apiVerifyTotp, apiGet, apiPost, decodeJwtPayload, getMe, apiPut, apiPatch, apiDelete, toast, toastOk, toastErr, toastInfo, ToastContainer, EmptyState, LoadingSkeleton, ConfirmModal, C, F, M, DEPTS, EMPLOYEES, PIPELINE_STEPS, MRP, MZP, DEMO_ENABLED, calcPayroll, VERIFY_SALARY, VERIFY_EXPECTED, verifyTaxFormulas, FUNNEL, S, STATUS_COLORS, STATUS_LABELS_RU, StatusBadge, SortableHeader, SearchInput, FilterBar, DetailPanel, PageHeader, ResourceTable, sortRows, filterRows, fmtNumRu, fmtDate, todayISO, loadDepartmentOptions, loadPositionOptions, loadGradeOptions, loadEmployeeOptions, loadWorkLocationOptions, loadStaffPositionOptions, loadPeriodOptions, useRefOptions, useMyTasks , InlineMsg , GL_MONTHS , useFormDraft , PrintPreview , getNavRecent , pushNavRecent , getNavFavorites , toggleNavFavorite };
